import { ISuggestions } from 'types/thoughtriver';
import api from '../services/api';
import { Method, requestOptions } from '../services/api-types';
import {
    ISuggestionResource,
    ISuggestionResourceStatic,
} from './SuggestionResource-types';

const suggestionCache: {
    [nonAssessedPropertyId: string]: {
        count?: number;
        [pageNumber: number]: ISuggestions[];
    };
} = {};

const SuggestionResource: ISuggestionResourceStatic = class SuggestionResource
    implements ISuggestionResource
{
    static async getSuggestionCountForProperty(
        stream: string,
        nonAssessedPropertyId: string
    ) {
        let totalSuggestions = suggestionCache[nonAssessedPropertyId]?.count;

        if (totalSuggestions === undefined) {
            const suggestionsResponse =
                await SuggestionResource.getSuggestionsForProperty(
                    stream,
                    nonAssessedPropertyId,
                    1,
                    1
                );
            const suggestions = suggestionsResponse.data;
            totalSuggestions = suggestions?.length ?? 0;

            const lastPage =
                suggestionsResponse?.links?.last?.match(/\[number\]=(\d+)/);
            if (lastPage && lastPage[1]) {
                totalSuggestions = parseInt(lastPage[1], 10);
            }

            suggestionCache[nonAssessedPropertyId] = {
                count: totalSuggestions,
            };
        }

        return totalSuggestions;
    }

    static async getSuggestionsForProperty(
        stream: string,
        nonAssessedPropertyId: string,
        pageNumber: number,
        pageSize: number
    ): Promise<ISuggestions> {
        const url =
            '/suggestions' +
            '?filter[remediation]=true' +
            `&filter[property-ids]=${nonAssessedPropertyId}` +
            `&page[number]=${pageNumber}` +
            `&page[size]=${pageSize}`;

        let suggestions =
            suggestionCache[nonAssessedPropertyId]?.[pageNumber]?.[pageSize];

        const options = {
            ...requestOptions,
            streamCode: stream,
            baseUrl: window.__APP_CONFIG__.apiPHPServiceUrl,
        };

        if (suggestions === undefined) {
            suggestions = (await (
                await api.request(Method.GET, url, options)
            )?.json()) as ISuggestions;

            suggestionCache[nonAssessedPropertyId] = {
                ...suggestionCache[nonAssessedPropertyId],
                [pageNumber]:
                    suggestionCache[nonAssessedPropertyId]?.[pageNumber] ?? [],
            };

            suggestionCache[nonAssessedPropertyId][pageNumber][pageSize] =
                suggestions;
        }

        return suggestions;
    }

    static async getSuggestionsAndTotalCountForProperty(
        stream: string,
        nonAssessedPropertyId: string,
        page: number,
        pageSize: number
    ): Promise<{ suggestions: ISuggestions; count: number }> {
        const [count, suggestions] = await Promise.all([
            SuggestionResource.getSuggestionCountForProperty(
                stream,
                nonAssessedPropertyId
            ),
            SuggestionResource.getSuggestionsForProperty(
                stream,
                nonAssessedPropertyId,
                page,
                pageSize
            ),
        ]);

        return {
            count,
            suggestions,
        };
    }
};

export default SuggestionResource;
